import React from 'react';
import PropTypes from 'prop-types';
import {
  FaCubes,
  FaLock,
  FaLayerGroup,
  FaShieldAlt,
  FaArrowRight,
  FaClock,
  FaDatabase
} from 'react-icons/fa';
import ModuleBreakdownDoughnut, { formatModuleName } from '../charts/ModuleBreakdownDoughnut';
import MetricKpiCard from '../MetricKpiCard';
import {
  formatBytes,
  formatDimensionName,
  formatConfidenceLevel,
  formatDimensionQuantity,
  formatUtcDateRange
} from '../../shared/analyticsViewUtils';

export default function ModuleUsageTab({
  moduleUsage = {},
  loading = false,
  entitlementDenied = false,
  onNavigateToPipeline
}) {
  const rawModules = Array.isArray(moduleUsage?.data) ? moduleUsage.data : [];
  const isEntitled = moduleUsage?.isEntitled !== false && !entitlementDenied;

  // Render upgrade gate if entitlement is denied
  if (!isEntitled) {
    return (
      <div className="tab-content-pane module-usage-tab" data-testid="module-usage-tab-locked">
        <div className="entitlement-upgrade-card" data-testid="module-upgrade-notice">
          <div className="upgrade-icon-wrap">
            <FaLock className="upgrade-lock-icon" />
          </div>
          <h3 className="upgrade-title">Advanced Analytics License Required</h3>
          <p className="upgrade-description">
            Detailed telemetry breakdowns across IAP modules (Admin Portal, Content Studio, Runtime Player, Extension Player)
            require the <strong>Advanced Analytics</strong> capability.
          </p>
          <div className="upgrade-feature-list">
            <div className="upgrade-feature-item">
              <FaShieldAlt className="feature-check-icon" />
              <span>Full volume breakdown across client execution runtimes</span>
            </div>
            <div className="upgrade-feature-item">
              <FaShieldAlt className="feature-check-icon" />
              <span>Dimensional tracking for raw vs aggregated confidence levels</span>
            </div>
            <div className="upgrade-feature-item">
              <FaShieldAlt className="feature-check-icon" />
              <span>Cross-runtime adoption auditing and license optimization</span>
            </div>
          </div>
          <div className="upgrade-actions">
            {onNavigateToPipeline && (
              <button
                type="button"
                className="btn btn-secondary-outline"
                onClick={onNavigateToPipeline}
                data-testid="upgrade-to-pipeline-btn"
              >
                View Pipeline Health <FaArrowRight />
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Separate activity event counts from storage bytes
  let totalActivityEvents = 0;
  let totalStorageBytes = 0;
  const activeModuleList = [];

  rawModules.forEach((m) => {
    const key = m.moduleKey || 'unknown';
    const name = formatModuleName(key);
    let moduleEvents = 0;
    let moduleStorage = 0;

    if (Array.isArray(m.dimensions) && m.dimensions.length > 0) {
      m.dimensions.forEach((dim) => {
        const q = Number(dim.quantity || 0);
        if (String(dim.unit).toLowerCase() === 'bytes' || String(dim.dimension).toLowerCase() === 'storage') {
          moduleStorage += q;
        } else {
          moduleEvents += q;
        }
      });
    } else {
      moduleEvents += Number(m.quantity || 0);
    }

    totalActivityEvents += moduleEvents;
    totalStorageBytes += moduleStorage;

    if (moduleEvents > 0 || moduleStorage > 0) {
      activeModuleList.push({
        key,
        name,
        events: moduleEvents,
        storage: moduleStorage,
        storageFormatted: formatBytes(moduleStorage)
      });
    }
  });

  // Flattened dimension records for table
  const dimensionRows = [];
  rawModules.forEach((m) => {
    const modKey = m.moduleKey || 'unknown';
    const modName = formatModuleName(modKey);
    const windowText = formatUtcDateRange(m.windowStartUtc, m.windowEndUtc);

    if (Array.isArray(m.dimensions) && m.dimensions.length > 0) {
      m.dimensions.forEach((dim, idx) => {
        const rawDim = dim.dimension || 'FeatureUsage';
        const rawQty = Number(dim.quantity || 0);
        const rawUnit = dim.unit || 'count';
        const qtyObj = formatDimensionQuantity(rawQty, rawUnit, rawDim);
        const confObj = formatConfidenceLevel(dim.confidence);

        dimensionRows.push({
          id: `${modKey}-${rawDim}-${idx}`,
          moduleKey: modKey,
          moduleName: modName,
          dimension: rawDim,
          dimensionDisplay: formatDimensionName(rawDim),
          quantityFormatted: qtyObj.formatted,
          quantityRaw: qtyObj.raw,
          confidenceLabel: confObj.label,
          confidenceClass: confObj.className,
          window: windowText
        });
      });
    } else {
      const rawDim = 'FeatureUsage';
      const rawQty = Number(m.quantity || 0);
      const rawUnit = 'count';
      const qtyObj = formatDimensionQuantity(rawQty, rawUnit, rawDim);
      const confObj = formatConfidenceLevel('RawFallback');

      dimensionRows.push({
        id: `${modKey}-default`,
        moduleKey: modKey,
        moduleName: modName,
        dimension: rawDim,
        dimensionDisplay: formatDimensionName(rawDim),
        quantityFormatted: qtyObj.formatted,
        quantityRaw: qtyObj.raw,
        confidenceLabel: confObj.label,
        confidenceClass: confObj.className,
        window: windowText
      });
    }
  });

  return (
    <div className="tab-content-pane module-usage-tab" data-testid="module-usage-tab">
      {/* Top KPI row */}
      <div className="kpi-grid">
        <MetricKpiCard
          title="Active Modules"
          value={activeModuleList.length.toLocaleString()}
          subtitle="Client runtimes reporting telemetry"
          icon={<FaCubes />}
          color="indigo"
          loading={loading}
          tooltip="Client runtime modules currently sending telemetry events (e.g. Admin Portal, Content Studio, Player, Extension)."
          badge={
            activeModuleList.length > 0 ? (
              <div className="active-module-chips">
                {activeModuleList.map((mod) => (
                  <span key={mod.key} className="module-chip-tag" data-testid={`module-chip-${mod.key}`}>
                    {mod.name}: <strong>{mod.events > 0 ? `${mod.events.toLocaleString()} events` : mod.storageFormatted}</strong>
                  </span>
                ))}
              </div>
            ) : null
          }
        />
        <MetricKpiCard
          title="Total Activity Events"
          value={totalActivityEvents.toLocaleString()}
          subtitle="Creator actions and player interactions"
          icon={<FaLayerGroup />}
          color="blue"
          loading={loading}
          tooltip="Cumulative volume of all user actions and operational telemetry events captured across active modules (excluding file storage)."
        />
        <MetricKpiCard
          title="Total Storage Used"
          value={formatBytes(totalStorageBytes)}
          subtitle="Assets and media storage consumed"
          icon={<FaDatabase />}
          color="emerald"
          loading={loading}
          tooltip={`Cumulative storage volume consumed by IAP assets (${totalStorageBytes.toLocaleString()} bytes).`}
        />
      </div>

      {/* Doughnut Chart */}
      <div className="charts-single-grid">
        <ModuleBreakdownDoughnut moduleUsage={rawModules} height={320} />
      </div>

      {/* Table Card */}
      <div className="analytics-table-card" data-testid="module-usage-table-card">
        <div className="table-card-header">
          <div>
            <h3 className="table-card-title">Module Dimensional Telemetry</h3>
            <p className="table-card-subtitle">Volume, confidence rating, and measurement windows by module dimension</p>
          </div>
        </div>

        <div className="analytics-table-responsive">
          <table className="analytics-table" data-testid="module-usage-table">
            <thead>
              <tr>
                <th>Module</th>
                <th>Dimension</th>
                <th className="text-right">Quantity</th>
                <th>Confidence Level</th>
                <th>Measurement Window</th>
              </tr>
            </thead>
            <tbody>
              {dimensionRows.length === 0 ? (
                <tr>
                  <td colSpan={5} className="table-empty-row" data-testid="module-table-empty">
                    No module telemetry dimensions recorded for this site collection.
                  </td>
                </tr>
              ) : (
                dimensionRows.map((row) => (
                  <tr key={row.id} data-testid={`module-row-${row.moduleKey}`}>
                    <td className="module-name-cell">
                      <strong>{row.moduleName}</strong>
                      <code className="module-key-badge">{row.moduleKey}</code>
                    </td>
                    <td>
                      <span className="dimension-chip">{row.dimensionDisplay}</span>
                    </td>
                    <td className="text-right font-medium" title={row.quantityRaw}>
                      {row.quantityFormatted}
                    </td>
                    <td>
                      <span className={`confidence-badge ${row.confidenceClass}`}>
                        {row.confidenceLabel}
                      </span>
                    </td>
                    <td className="text-muted" style={{ fontSize: '12px' }}>
                      <FaClock style={{ marginRight: '6px', verticalAlign: '-1px' }} />
                      {row.window}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

ModuleUsageTab.propTypes = {
  moduleUsage: PropTypes.shape({
    data: PropTypes.array,
    isEntitled: PropTypes.bool,
    error: PropTypes.any
  }),
  loading: PropTypes.bool,
  entitlementDenied: PropTypes.bool,
  onNavigateToPipeline: PropTypes.func
};
